import React from "react";
import { useTranslation } from "react-i18next";
import Dropdown from "components/dropdown";
import LanguageSwitcher from "components/switcher/LanguageSwitcher";
import { FiAlignJustify, FiLogOut, FiUser } from "react-icons/fi";
import { Link, matchPath, useLocation, useNavigate } from "react-router-dom";
import { RiMoonFill, RiSunFill } from "react-icons/ri";
import { useAppDispatch, useAppSelector } from "reduxFiles/hooks";
import { logout } from "reduxFiles/slices/authSlice";
import { useLogoutMutation } from "reduxFiles/api/authApi";
import { RoutesType } from "types/globalTypes";
import routes from "routes";
import ConfirmModal from "components/modal/ConfirmModal";
import { useModal } from "hooks/useModal";
import { API_URL, baseApi } from "reduxFiles/api/baseApi";
import { clearResponderAuth } from "reduxFiles/slices/responderAuthSlice";
import { UserRole } from "types/userTypes";
import { PanelSwitcher } from "components/switcher/PanelSwitcher";
import { responderApi } from "reduxFiles/api/responderApi";

const Navbar = ({
  onOpenSidenav,
  secondary,
  showProfile = true,
}: {
  onOpenSidenav: () => void;
  secondary?: boolean | string;
  showProfile?: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [darkmode, setDarkmode] = React.useState(false);
  const profile = useAppSelector((state) => state.auth.user);

  const findActiveRoute = (routes: RoutesType[], pathname: string) => {
    for (const route of routes) {
      const fullPath = `${route.layout}/${route.path}`;

      if (matchPath({ path: fullPath, end: true }, pathname)) {
        return route;
      }

      if (route.children) {
        for (const child of route.children) {
          const childPath = `${child.layout}/${child.path}`;

          if (matchPath({ path: childPath, end: true }, pathname)) {
            return child;
          }
        }
      }
    }

    return null;
  };

  const activeRoute = findActiveRoute(routes, location.pathname);

  // logout
  const confirmModal = useModal();
  const [logoutMutation, { isLoading }] = useLogoutMutation();

  const logoutHandler = async () => {
    try {
      await logoutMutation().unwrap();
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(baseApi.util.resetApiState());
      dispatch(logout());
    }
  };

  const logoutResponderHandler = async () => {
    await dispatch(clearResponderAuth());
    dispatch(responderApi.util.resetApiState());
  };

  return (
    <nav className="sticky top-4 z-40 flex flex-row flex-wrap items-center justify-between rounded-xl bg-white/10 p-2 backdrop-blur-xl dark:bg-[#0b14374d]">
      <div className="ml-[6px]">
        <div className="h-6 w-[224px] pt-1">
          <Link
            className="text-sm font-normal text-navy-700 hover:underline dark:text-white dark:hover:text-white"
            to=" "
          >
            {t("pages.pages")}
            <span className="mx-1 text-sm text-navy-700 hover:text-navy-700 dark:text-white">
              {" "}
              /{" "}
            </span>
          </Link>
          <Link
            className="text-sm font-normal capitalize text-navy-700 hover:underline dark:text-white dark:hover:text-white"
            to="#"
          >
            {t(`pages.${activeRoute?.name || ""}`)}
          </Link>
        </div>
        <p className="my-1 shrink text-[28px] capitalize text-navy-700 dark:text-white">
          <Link
            to="#"
            className="font-bold capitalize hover:text-navy-700 dark:hover:text-white"
          >
            {t(`pages.${activeRoute?.name || ""}`)}
          </Link>
        </p>
      </div>

      <div className="relative mt-[3px] flex h-[61px] flex-grow items-center justify-around gap-2 rounded-full bg-white px-4 py-2 shadow-xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none md:flex-grow-0 md:gap-2 xl:gap-4">
        {/* <div className="flex h-full items-center rounded-full bg-lightPrimary text-navy-700 dark:bg-navy-900 dark:text-white xl:w-[225px]">
          <p className="pl-3 pr-2 text-xl">
            <FiSearch className="h-4 w-4 text-gray-400 dark:text-white" />
          </p>
          <input
            type="text"
            placeholder={t("searchPlaceholder")}
            className="block h-full w-full rounded-full bg-lightPrimary text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white sm:w-fit"
          />
        </div> */}
        <LanguageSwitcher />

        {showProfile && <PanelSwitcher />}

        <span
          className="flex cursor-pointer text-xl text-gray-600 dark:text-white xl:hidden"
          onClick={onOpenSidenav}
        >
          <FiAlignJustify className="h-5 w-5" />
        </span>
        {/* start Notification */}
        {/* <Dropdown
          button={
            <p className="cursor-pointer">
              <IoMdNotificationsOutline className="h-5 w-5 text-gray-600 dark:text-white" />
            </p>
          }
          animation="origin-[65%_0%] md:origin-top-right transition-all duration-300 ease-in-out"
          children={
            <div className="flex w-[360px] flex-col gap-3 rounded-[20px] bg-white p-4 shadow-xl shadow-shadow-500 dark:!bg-navy-700 dark:text-white dark:shadow-none sm:w-[460px]">
              <div className="flex items-center justify-between">
                <p className="text-base font-bold text-navy-700 dark:text-white">
                  {t("notification.notif")}
                </p>
                <p className="text-sm font-bold text-navy-700 dark:text-white">
                  {t("notification.markAllAsRead")}
                </p>
              </div>

              <button className="flex w-full items-center">
                <div className="flex h-full w-[85px] items-center justify-center rounded-xl bg-gradient-to-b from-brandLinear to-brand-500 py-4 text-2xl text-white">
                  <BsArrowBarUp />
                </div>
                <div className="ml-2 flex h-full w-full flex-col justify-center rounded-lg px-1 text-sm">
                  <p className="mb-1 text-left text-base font-bold text-gray-900 dark:text-white">
                    New Update: Horizon UI Dashboard PRO
                  </p>
                  <p className="font-base text-left text-xs text-gray-900 dark:text-white">
                    A new update for your downloaded item is available!
                  </p>
                </div>
              </button>

              <button className="flex w-full items-center">
                <div className="flex h-full w-[85px] items-center justify-center rounded-xl bg-gradient-to-b from-brandLinear to-brand-500 py-4 text-2xl text-white">
                  <BsArrowBarUp />
                </div>
                <div className="ml-2 flex h-full w-full flex-col justify-center rounded-lg px-1 text-sm">
                  <p className="mb-1 text-left text-base font-bold text-gray-900 dark:text-white">
                    New Update: Horizon UI Dashboard PRO
                  </p>
                  <p className="font-base text-left text-xs text-gray-900 dark:text-white">
                    A new update for your downloaded item is available!
                  </p>
                </div>
              </button>
            </div>
          }
          classNames={
            "py-2 top-4 ltr:-left-[230px] rtl:-right-[230px] ltr:md:-left-[440px] rtl:md:-right-[440px] w-max"
          }
        /> */}

        {/* start Horizon PRO */}
        {/* <Dropdown
          button={
            <p className="cursor-pointer">
              <IoMdInformationCircleOutline className="h-5 w-5 text-gray-600 dark:text-white" />
            </p>
          }
          children={
            <div className="flex w-[350px] flex-col gap-2 rounded-[20px] bg-white p-4 shadow-xl shadow-shadow-500 dark:!bg-navy-700 dark:text-white dark:shadow-none">
              <div
                style={{
                  backgroundImage: `url(${navbarimage})`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                }}
                className="mb-2 aspect-video w-full rounded-lg"
              />
              <a
                target="blank"
                href="https://horizon-ui.com/pro?ref=live-free-tailwind-react"
                className="px-full linear flex cursor-pointer items-center justify-center rounded-xl bg-brand-500 py-[11px] font-bold text-white transition duration-200 hover:bg-brand-600 hover:text-white active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:bg-brand-200"
              >
               buyPro
              </a>
              <a
                target="blank"
                href="https://horizon-ui.com/docs-tailwind/docs/react/installation?ref=live-free-tailwind-react"
                className="px-full linear flex cursor-pointer items-center justify-center rounded-xl border py-[11px] font-bold text-navy-700 transition duration-200 hover:bg-gray-200 hover:text-navy-700 dark:!border-white/10 dark:text-white dark:hover:bg-white/20 dark:hover:text-white dark:active:bg-white/10"
              >
               seeDocs
              </a>
              <a
                target="blank"
                href="https://horizon-ui.com/?ref=live-free-tailwind-react"
                className="hover:bg-black px-full linear flex cursor-pointer items-center justify-center rounded-xl py-[11px] font-bold text-navy-700 transition duration-200 hover:text-navy-700 dark:text-white dark:hover:text-white"
              >
             tryFree
              </a>
            </div>
          }
          classNames={
            "py-2 top-6 ltr:-left-[250px] rtl:-right-[250px] ltr:md:-left-[330px] rtl:md:-right-[330px] w-max"
          }
          animation="origin-[75%_0%] md:origin-top-right transition-all duration-300 ease-in-out"
        /> */}

        <div
          className="cursor-pointer text-gray-600"
          onClick={() => {
            if (darkmode) {
              document.body.classList.remove("dark");
              setDarkmode(false);
            } else {
              document.body.classList.add("dark");
              setDarkmode(true);
            }
          }}
        >
          {darkmode ? (
            <RiSunFill className="h-5 w-5 text-gray-600 dark:text-white" />
          ) : (
            <RiMoonFill className="h-5 w-5 text-gray-600 dark:text-white" />
          )}
        </div>

        {/* logout responder */}
        {!showProfile && (
          <p className="cursor-pointer" onClick={confirmModal.openModal}>
            <FiLogOut className="h-5 w-5 text-gray-600 dark:text-white" />
          </p>
        )}

        {/* Profile & Dropdown */}
        {showProfile && (
          <Dropdown
            button={
              profile?.avatar ? (
                <img
                  src={`${API_URL}/file/${profile?.avatar}`}
                  alt={profile?.fullname || "avatarUser"}
                  className="h-12 w-12 cursor-pointer rounded-full border border-brand-500"
                />
              ) : (
                <p className="cursor-pointer">
                  <FiUser className="h-10 w-10 cursor-pointer rounded-full border border-brand-500 text-gray-600 dark:text-white" />
                </p>
              )
            }
            children={
              <div className="flex w-56 flex-col justify-start rounded-[20px] bg-white bg-cover bg-no-repeat p-3 shadow-xl shadow-shadow-500 dark:!bg-navy-700 dark:text-white dark:shadow-none">
                <div className="rounded-2xl bg-gray-50 p-2 dark:bg-white/5">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t("profile.welcomUser")}
                  </p>

                  <p className="mt-1 font-semibold text-navy-700 dark:text-white">
                    {profile?.fullname}
                  </p>
                </div>

                <div className="mt-1 h-px w-full bg-gray-200 dark:bg-white/20 " />

                <div className="flex flex-col gap-1">
                  {profile?.role === UserRole.USER && (
                    <button
                      onClick={() => {
                        navigate("/company/profile");
                      }}
                      className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-700 transition-all duration-200 hover:bg-gray-100 hover:text-brand-500 dark:text-white dark:hover:bg-white/10"
                    >
                      <FiUser className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
                      <span>{t("profile.profileEdit")}</span>
                    </button>
                  )}

                  {/* <button className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-700 transition-all duration-200 hover:bg-gray-100 hover:text-brand-500 dark:text-white dark:hover:bg-white/10">
                    <FiSettings className="h-4 w-4 transition-transform duration-200 group-hover:rotate-90" />
                    <span>{t("profile.settings")}</span>
                  </button> */}

                  <button
                    onClick={() => confirmModal.openModal()}
                    className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-red-500 transition-all duration-200 hover:bg-red-50 dark:hover:bg-red-500/10"
                  >
                    <FiLogOut className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    <span>{t("profile.logout")}</span>
                  </button>
                </div>
              </div>
            }
            classNames={"py-2 top-8 ltr:-left-[180px] rtl:-right-[180px] w-max"}
          />
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => confirmModal.closeModal()}
        handler={!showProfile ? logoutResponderHandler : logoutHandler}
        loading={isLoading}
        title={
          !showProfile ? t("profile.responderLogout") : t("profile.logout")
        }
        desc={
          !showProfile
            ? t("profile.responderConfirmLogout")
            : t("profile.confirmLogout")
        }
      />
    </nav>
  );
};

export default Navbar;
